<script lang="ts" setup>
  import { defineProps, ref, onBeforeUnmount, defineEmits, computed, watch, onMounted } from 'vue'
  import { Upload } from 'lucide-vue-next'
  import {
    type CustomerCase,
    DocumentsService,
    UploadOriginEnum,
    type Document
  } from '@/types/generated'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { PropType } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { emitter } from '@/store/app/case/detail/eventEmitter'

  const emit = defineEmits<{
    (e: 'fileUploaded', documentUuid: string): void
    (e: 'fileAiReady', documentUuid: string): void
    (e: 'fileAiComplete', documentUuid: string): void
    (e: 'fileError', documentUuid: string, errorMessage: string): void
    (e: 'filesSelected', files: File[]): void
  }>()

  const props = defineProps({
    caseData: {
      type: Object as PropType<CustomerCase>,
      required: true
    },
    maxSize: {
      type: Number,
      default: 100
    },
    autoRemoveAfterUpload: {
      type: Boolean,
      default: false,
      description: 'Whether to automatically remove files after successful upload'
    },
    autoRemoveAfterAiReady: {
      type: Boolean,
      default: false,
      description: 'Whether to automatically remove files after AI processing stage 1 is done'
    },
    autoRemoveAfterAiComplete: {
      type: Boolean,
      default: false,
      description: 'Whether to automatically remove files after AI processing is complete'
    }
  })

  const { t } = useTranslation({
    en: {
      DataExtraction: 'Data Extraction',
      BasicAIAnalysis: 'Basic AI Analysis',
      Summary: 'Summary',
      AdvancedAIAnalysis: 'Advanced AI Analysis',
      SummaryEmbedding: 'Summary Embedding',
      Analysis: 'Analysis',
      Queued: 'Queued',
      Loading: 'Loading Documents',
      PreparingDocuments: 'Preparing your documents...',
      DropFilesHere: 'Drop your files here or',
      Browse: 'browse',
      MaximumSize: 'Maximum size',
      Error: 'Error',
      OCRError: 'OCR Error',
      EmbeddingsError: 'Embeddings Error',
      ProcessingError: 'Processing Error',
      DocumentError: 'An error occurred while processing your document. Please try again.'
    },
    de: {
      DataExtraction: 'Datenextraktion',
      BasicAIAnalysis: 'Grundlegende KI-Analyse',
      Summary: 'Zusammenfassung',
      AdvancedAIAnalysis: 'Fortgeschrittene KI-Analyse',
      SummaryEmbedding: 'Zusammenfassungs-Embedding',
      Analysis: 'Analyse',
      Queued: 'In Warteschlange',
      Loading: 'Dokumente werden geladen',
      PreparingDocuments: 'Ihre Dokumente werden vorbereitet...',
      DropFilesHere: 'Legen Sie Ihre Datei hier ab oder',
      Browse: 'durchsuchen',
      MaximumSize: 'Maximale Größe',
      Error: 'Fehler',
      OCRError: 'OCR-Fehler',
      EmbeddingsError: 'Embeddings-Fehler',
      ProcessingError: 'Verarbeitungsfehler',
      DocumentError:
        'Ein Fehler ist während der Verarbeitung Ihres Dokuments aufgetreten. Bitte versuchen Sie es erneut.'
    }
  })

  // Use the case store for document tracking
  const caseStore = useCaseStore()
  const fileInput = ref<HTMLInputElement | null>(null)
  const dropZone = ref<HTMLDivElement | null>(null)
  const progressIntervals = ref<Record<string, number>>({})
  const documentPollInterval = ref<number | null>(null)

  // Check for existing uploads when mounting
  onMounted(async () => {
    // Add event listener for document errors
    emitter.on('document-error', (event) => {
      console.log('DocumentUpload received document error event:', event)
      emit('fileError', event.documentUuid, event.errorMessage)
    })

    await caseStore.fetchCaseWithErrorHandling(props.caseData?.uuid)
    if (props.caseData?.uuid) {
      // Check for active uploads from the document module
      const activeUploads = caseStore.activeUploadsArray.filter(
        (upload) => upload.caseUuid === props.caseData.uuid
      )

      // Start progress simulation for any incomplete uploads
      startProgressSimulationForIncompleteUploads()

      // Set up document state listeners
      setupDocumentStateListeners()

      // Start polling for document updates
      startDocumentPolling()
    }
  })

  // Set up listeners for document state changes
  function setupDocumentStateListeners() {
    // First, clear existing watchers
    clearDocumentStateListeners()

    // For each document, watch for AI ready and complete states
    if (caseStore.caseRecord?.documents) {
      caseStore.caseRecord.documents.forEach((doc) => {
        if (doc.uuid) {
          // Check current states and emit events if needed
          if (caseStore.isDocumentAiReadyState(doc.uuid)) {
            emit('fileAiReady', doc.uuid)
          }

          if (caseStore.isDocumentAiCompleteState(doc.uuid)) {
            emit('fileAiComplete', doc.uuid)
          }

          // Check for critical errors using the store's logic
          if (
            caseStore.documentsWithCriticalErrors.some((errorDoc) => errorDoc.uuid === doc.uuid)
          ) {
            console.log('Document has critical error, emitting fileError event', doc.uuid)
            emit('fileError', doc.uuid, t('DocumentError'))
          }

          // Start tracking if document is still processing
          if (caseStore.isDocumentInProcess(doc.uuid)) {
            caseStore.trackDocument(doc.uuid)
          }
        }
      })
    }

    // Also check for errors in any documents that might have been missed
    checkDocumentsForErrors()
  }

  // Clear document state listeners
  function clearDocumentStateListeners() {
    // This is now handled by the caseStore
  }

  // Clean up on component unmount
  onBeforeUnmount(() => {
    // Remove event listener for document errors when component unmounts
    emitter.off('document-error')

    // Clear all progress simulation intervals
    Object.keys(progressIntervals.value).forEach((id) => {
      clearInterval(progressIntervals.value[id])
      delete progressIntervals.value[id]
    })

    // Stop document polling
    stopDocumentPolling()
  })

  // Event handlers for file selection
  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    const files = e.dataTransfer?.files
    if (files) {
      handleFiles(Array.from(files))
    }
  }

  const onFileInput = (e: Event) => {
    const input = e.target as HTMLInputElement
    if (input.files) {
      handleFiles(Array.from(input.files))
    }
  }

  // Process selected files
  const handleFiles = (files: File[]) => {
    if (files.length > 0) {
      emit('filesSelected', files)
    }

    files.forEach((file) => {
      if (file.size > props.maxSize * 1024 * 1024) {
        // TODO: Show error message
        return
      }

      // Register the upload with the document module
      const uploadId = caseStore.registerUpload(props.caseData.uuid, file.name, file.size)

      // Start the upload
      uploadFile(file, uploadId)
    })
  }

  // Upload file to server
  const uploadFile = async (file: File, uploadId: string) => {
    try {
      // Update progress in the document module
      caseStore.updateUploadProgress(uploadId, 0)

      // Start the progress simulation
      const progressInterval = startProgressSimulation(uploadId)

      // Actual upload
      const response = await DocumentsService.documentsUploadCreate({
        caseUuid: props.caseData.uuid,
        formData: {
          file: file,
          upload_origin: UploadOriginEnum.MANUELL_HINZUGEFÜGT
        }
      })

      // Clear the interval when upload is done
      clearInterval(progressInterval)
      if (uploadId && progressIntervals.value[uploadId]) {
        delete progressIntervals.value[uploadId]
      }

      // Update the document module
      caseStore.updateUploadProgress(uploadId, 100)

      // Track document in the store
      if (response.uuid) {
        caseStore.trackDocument(response.uuid)
        caseStore.completeUpload(uploadId, response.uuid, response)
        emit('fileUploaded', response.uuid)
      }

      if (props.autoRemoveAfterUpload) {
        setTimeout(() => {
          caseStore.removeUpload(uploadId)
        }, 1000)
      }
    } catch (error: any) {
      // Set error for the upload
      const errorMessage = error?.body?.file?.[0] || error?.body?.detail || 'Upload failed'
      caseStore.setUploadError(uploadId, errorMessage)

      // Clear any progress simulation
      if (uploadId && progressIntervals.value[uploadId]) {
        clearInterval(progressIntervals.value[uploadId])
        delete progressIntervals.value[uploadId]
      }
    }
  }

  // Start progress simulation for all incomplete uploads
  const startProgressSimulationForIncompleteUploads = () => {
    caseStore.activeUploadsArray.forEach((upload) => {
      if (upload.caseUuid === props.caseData.uuid && upload.progress < 100 && !upload.error) {
        // Only start a new interval if one doesn't already exist for this upload
        if (!progressIntervals.value[upload.id]) {
          startProgressSimulation(upload.id)
        }
      }
    })
  }

  // Start progress simulation for a specific file
  const startProgressSimulation = (uploadId: string) => {
    // Clear any existing interval for this upload
    if (progressIntervals.value[uploadId]) {
      clearInterval(progressIntervals.value[uploadId])
    }

    // Start the progress simulation
    const interval = window.setInterval(() => {
      const upload = caseStore.activeUploadsArray.find((u) => u.id === uploadId)

      // If upload is gone or completed, clear the interval
      if (!upload || upload.progress >= 100 || upload.error) {
        clearInterval(interval)
        delete progressIntervals.value[uploadId]
        return
      }

      // Increment progress by a small amount, but don't go over 90%
      // We'll set it to 100% when the upload is actually complete
      if (upload.progress < 90) {
        // Slow down the progress as it gets higher to simulate real upload behavior
        const increment = upload.progress < 30 ? 2 : upload.progress < 60 ? 1 : 0.5
        const newProgress = Math.min(90, upload.progress + increment)

        // Update the document module
        caseStore.updateUploadProgress(uploadId, newProgress)
      }
    }, 300) // Update every 300ms (slower than before)

    // Store the interval ID
    progressIntervals.value[uploadId] = interval

    return interval
  }

  // Start polling for document updates
  function startDocumentPolling() {
    if (documentPollInterval.value) return

    // Do an immediate check first
    checkDocumentsForErrors()

    documentPollInterval.value = window.setInterval(() => {
      checkDocumentsForErrors()
    }, 3000) // Poll every 3 seconds
  }

  // Check documents for errors
  async function checkDocumentsForErrors() {
    if (props.caseData?.uuid) {
      // Check if there are any documents in processing state
      const hasProcessingDocuments = caseStore.processingDocuments.length > 0

      // If no documents are in processing state and no active uploads, stop polling
      if (
        !hasProcessingDocuments &&
        !caseStore.activeUploadsArray.some((u) => u.caseUuid === props.caseData?.uuid)
      ) {
        console.log('No documents in processing state and no active uploads, stopping polling')
        stopDocumentPolling()
        return
      }

      // Refresh case data
      await caseStore.fetchCaseWithErrorHandling(props.caseData?.uuid)

      // Check for document errors in all documents, not just processing ones
      if (caseStore.caseRecord?.documents) {
        caseStore.caseRecord.documents.forEach((doc) => {
          if (doc.uuid) {
            // Check for critical errors using the store's logic
            if (
              caseStore.documentsWithCriticalErrors.some((errorDoc) => errorDoc.uuid === doc.uuid)
            ) {
              console.log('Document has critical error from polling', doc.uuid)
              emit('fileError', doc.uuid, t('DocumentError'))
            }
          }
        })
      }
    }
  }

  // Stop polling for document updates
  function stopDocumentPolling() {
    if (documentPollInterval.value) {
      clearInterval(documentPollInterval.value)
      documentPollInterval.value = null
    }
  }

  // Prevent default browser behavior for drag events
  const preventDefaults = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
  }
</script>

<template>
  <div
    ref="dropZone"
    class="flex cursor-pointer justify-center rounded-xl border border-dashed border-gray-300 bg-white p-6"
    @dragover.prevent="preventDefaults"
    @dragenter.prevent="preventDefaults"
    @dragleave.prevent="preventDefaults"
    @drop="onDrop"
    @click="fileInput?.click()"
  >
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      multiple
      @change="onFileInput"
    />
    <div class="text-center">
      <span
        class="inline-flex size-12 items-center justify-center rounded-full bg-gray-100 text-gray-800"
      >
        <Upload class="size-5 shrink-0" />
      </span>

      <div class="mt-2 flex flex-wrap justify-center text-sm leading-6 text-gray-600">
        <span class="pe-1 font-medium text-gray-800">{{ t('DropFilesHere') }}</span>
        <span
          class="rounded-lg bg-white font-semibold text-purple-600 decoration-2 focus-within:outline-none
            focus-within:ring-2 focus-within:ring-purple-600 focus-within:ring-offset-2 hover:text-purple-700
            hover:underline"
        >
          {{ t('Browse') }}
        </span>
      </div>

      <p class="mt-1 text-xs text-gray-400">{{ t('MaximumSize') }} {{ props.maxSize }} MB.</p>
    </div>
  </div>
</template>
